<template>
  <div class="keyboard">
    <CyberboardR6CdcPanel
      ref="cdcPanel"
      :baseData="profileNew.data"
      :defaultData="profileNew.default"
      :syncStatus="keySyncStatus"
      @ready="onCdcReady"
      @state="onCdcState"
      @data="onCdcData"
      @error="onCdcError"
    />
    <CyberboardR6Navigation />
    <div class="fixed-panel px">
      <div class="keyboard-panel">
        <c-keyboard-new
          class="c-keyboard"
          :layer="layer"
          :layerIndex="layerIndex"
          :selectedIndex="selectedIndex"
          @key-click="onKeyClick"
        />
      </div>
      <div class="radio-panel">
        <a-radio-group v-model="layerIndex" size="large">
          <a-radio-button
            v-for="index in layerNum"
            :key="index"
            :value="index - 1"
          >
            {{ index === 1 ? $t("keyboard.layer") + " " + index : index }}
          </a-radio-button>
        </a-radio-group>
        <a-radio-group
          :key="virtualRadioKey"
          class="virtual-radio"
          :value="type"
          size="large"
          @change="onToolSelect"
        >
          <a-radio-button key="keymap" value="keymap">{{
            $t("keymap")
          }}</a-radio-button>
          <a-radio-button key="press&hold" value="press&hold">{{
            $t("press&hold")
          }}</a-radio-button>
          <a-radio-button key="macro" value="macro">{{
            $t("macro")
          }}</a-radio-button>
          <a-radio-button key="reset" value="reset">{{
            $t("reset")
          }}</a-radio-button>
        </a-radio-group>
      </div>
    </div>
    <div class="virtual-keyboard-panel">
      <c-virtual-keyboard-new
        class="c-virtual-keyboard"
        v-if="type === 'keymap'"
        :selectedKey="layer[selectedKeyIndex]"
        :layerNum="layerNum"
        :layerIndex="layerIndex"
        @key-click="onVirtualKeyClick"
      />
      <c-press-hold
        v-if="type === 'press&hold'"
        :dispatchType="'profileNew/setData'"
        :profile="profileNew"
        :selectedKey="layer[selectedKeyIndex]"
        :selectedKeyIndex="selectedKeyIndex"
        :layerIndex="layerIndex"
        :pressKeys="pressKeys"
        :holdKeys="holdKeys"
        @key-click="onVirtualKeyClick"
      />
      <c-macro
        v-if="type === 'macro'"
        :dispatchType="'profileNew/setData'"
        :profile="profileNew"
        :selectedKey="layer[selectedKeyIndex]"
        :selectedKeyIndex="selectedKeyIndex"
        :layerIndex="layerIndex"
        :keys="basicKeys"
        :syncMacro="false"
        @key-click="onVirtualKeyClick"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { deepCopy } from "@/utils";
import { anyKeys } from "@/components/SpecialKeys";
import {
  pressKeys,
  holdKeys,
  basicKeys,
} from "@/views/CyberboardNew/components/VirtualKeyboardNew";
import cyberboardR6SyncMixin from "./utils/cyberboardR6SyncMixin";
import CyberboardR6CdcPanel from "./CyberboardR6CdcPanel.vue";
import CyberboardR6Navigation from "./CyberboardR6Navigation.vue";

export default {
  name: "KeyboardR6",
  components: {
    CyberboardR6CdcPanel,
    CyberboardR6Navigation,
  },
  mixins: [cyberboardR6SyncMixin],
  data() {
    return {
      layerIndex: 0,
      selectedIndex: -2,
      selectedKeyIndex: null,
      type: "keymap",
      virtualRadioKey: 0,
      profileDataToSkipAutoSync: null,
      pressKeys,
      holdKeys,
      basicKeys,
    };
  },
  computed: {
    ...mapState(["profileNew"]),
    layer() {
      return this.profileNew.data.key_layer.layer_data[this.layerIndex].layer;
    },
    layerNum() {
      return this.profileNew.data.key_layer.layer_num;
    },
  },
  watch: {
    "profileNew.data": {
      deep: true,
      handler(data) {
        if (data === this.profileDataToSkipAutoSync) {
          this.profileDataToSkipAutoSync = null;
          return;
        }
        this.enqueueProfileChangeSync(data, [
          "keymap",
          "macro",
          "pressHold",
          "keyFunctions",
        ]);
      },
    },
    layerIndex(index) {
      if (index === 1 && [0, 20].includes(this.selectedIndex)) {
        this.selectedIndex = -1;
        this.selectedKeyIndex = null;
      }
    },
  },
  methods: {
    onKeyClick({ index, keyIndex }) {
      this.selectedKeyIndex = this.selectedIndex === index ? null : keyIndex;
      this.selectedIndex = this.selectedIndex === index ? -1 : index;
    },
    onVirtualKeyClick(key) {
      if (this.selectedIndex < 0 || this.selectedKeyIndex === null) return;
      const selectedKey = this.layer[this.selectedKeyIndex];
      const data = deepCopy(this.profileNew.data);
      if (anyKeys.includes(selectedKey)) {
        const index = data.tab_key.findIndex(
          (item) => item.ph_key_data === selectedKey
        );
        if (index > -1) data.tab_key.splice(index, 1);
      }
      const layer = [...data.key_layer.layer_data[this.layerIndex].layer];
      layer[this.selectedKeyIndex] = key;
      data.key_layer.layer_data[this.layerIndex].layer = layer;
      this.$store.dispatch("profileNew/setData", data);
    },
    onToolSelect(event) {
      const selectedType = event.target.value;
      if (selectedType === "reset") {
        this.onReset();
        this.virtualRadioKey += 1;
        return;
      }
      this.type = selectedType;
    },
    async onReset() {
      this.$confirm({
        title: this.$t("keyboard.reset"),
        okText: this.$t("yes"),
        cancelText: this.$t("cancel"),
        centered: true,
        onOk: async () => {
          const reconnect = this.connectionState === "connected";
          if (reconnect) await this.r6Bridge.resetKeyboard();
          const data = {
            ...this.profileNew.data,
            key_layer: deepCopy(this.profileNew.default.key_layer),
            tab_key: deepCopy(this.profileNew.default.tab_key),
            macro_key: deepCopy(this.profileNew.default.macro_key),
          };
          this.layerIndex = 0;
          this.selectedIndex = -2;
          this.selectedKeyIndex = null;
          this.type = "keymap";
          this.profileDataToSkipAutoSync = data;
          await this.$store.dispatch("profileNew/setData", data);
          if (reconnect) {
            await this.$nextTick();
            await this.$refs.cdcPanel.reconnectAfterReset();
          }
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.keyboard {
  min-height: inherit;
  text-align: center;
  color: #ffffff;

  .fixed-panel.px {
    width: 100%;
    background: #171717;
    padding: $header-height + 20px 0 20px 0;
    position: fixed;
    z-index: 1;
    top: 0;
  }

  .keyboard-panel {
    @include flex-center;
    margin-bottom: 20px;

    .c-keyboard {
      width: 1320px;
    }
  }

  .radio-panel {
    @include flex-center;
    width: 1300px;
    margin: 0 auto;
    justify-content: space-between;
    white-space: nowrap;
  }

  .virtual-keyboard-panel {
    @include flex-center;
    margin-top: calc(880px - 7.5vw);
    margin-bottom: 60px;
    position: relative;
    z-index: 0;

    .c-virtual-keyboard {
      width: 1320px;
    }
  }
}
</style>
